import CartApi from '@apis/cart/CartApi';
import queries from '@apis/queries';
import useCart from '@hooks/cart/useCart';
import useToast from '@hooks/ui/useToast';
import useAppCustom from '@hooks/useAppCustom';
import useProjectId from '@hooks/useProjectId';
import { AddToCartRequest } from '@models/cart';
import { ErrorResponse } from '@models/error';
import useCalculatedOrder from '@store/item/hooks/useCalculatedOrder';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

function useAddCart(shopId: string) {
    const { orderLines } = useCalculatedOrder();
    const projectId = useProjectId();
    const toast = useToast();
    const { cart } = useCart(shopId);
    const queryClient = useQueryClient();
    const { mutate } = useMutation({
        mutationFn: (request: AddToCartRequest) => CartApi.addToCart(shopId, request),
        async onSuccess() {
            await queryClient.invalidateQueries({ queryKey: queries.cart._def });
            toast.success('장바구니에 담겼습니다.');
        },
        onError(e: AxiosError<ErrorResponse>) {
            console.error(e);
            toast.error(e.response?.data?.message || '오류가 발생하였습니다.');
        },
    });
    const { itemMaxQuantity } = useAppCustom().item;

    const handleAddToCart = () => {
        if (orderLines.length !== 1) {
            toast.error('잘못된 접근입니다.');
            return;
        }
        const {
            quantity, defaultOptions, personalOptions, shopItemId,
        } = orderLines[0];
        // 최대 개수 확인
        const currentQty = cart?.items
            .find((item) => item.itemId === shopItemId)
            ?.qty || 0;
        if (currentQty + quantity > itemMaxQuantity) {
            toast.error(`상품은 최대 ${itemMaxQuantity}개까지 담을 수 있습니다.\n현재 장바구니에 담긴 수량: ${currentQty}개`);
            return;
        }

        const options = [...defaultOptions, ...personalOptions]
            .map((optionGroup) => optionGroup.options).flat();
        const optionIds = [...new Set(options.map((option) => option.shopOptionId))];

        const newOptions = optionIds.map((optionId) => {
            const matchedOptions = options.filter((option) => option.shopOptionId === optionId);
            const totalQuantity = matchedOptions.reduce((sum, option) => sum + option.quantity, 0);
            return {
                optionId, quantity: totalQuantity,
            };
        });
        const request: AddToCartRequest = {
            itemId: shopItemId,
            quantity,
            projectId,
            cartId: cart?.cartId,
            options: newOptions,
        };
        console.log({ request });
        mutate(request);
    };
    return {
        handleAddToCart,
    };
}

export default useAddCart;
